import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { templateOpeningPurpose } from '@/components/assessment-engine/templatePurpose';
import {
  templateRespondentFraming,
  type TemplateRespondentFramingDefaults,
} from '@/components/assessment-engine/templateRespondentFraming';
import {
  AE_BTN_PRIMARY,
  AE_INPUT,
  AE_INPUT_COMPACT,
  AE_LABEL,
  AE_LABEL_COMPACT,
  AE_MUTED,
  AE_SECTION_TITLE,
  AE_SECTION_TITLE_COMPACT,
  AE_SELECT,
  AE_SELECT_COMPACT,
} from '@/components/assessment-engine/layout/aeTheme';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeCampaign, AeTemplateSummary, AeRespondentConfig, CreateCampaignInput } from '@/types/assessmentEngine';
import { AE_DEFAULT_RESPONDENT_CONFIG } from '@/types/assessmentEngine';
import { CampaignRespondentSetupFields } from './CampaignRespondentSetupFields';
import {
  CampaignInviteSetupFields,
  type CampaignMessagingSetupValue,
} from './CampaignInviteSetupFields';
import { CampaignDistributionModeFields } from './CampaignDistributionModeFields';
import { CampaignInviteListFields } from './CampaignInviteListFields';
import { CampaignAdminDigestFields } from './CampaignAdminDigestFields';
import {
  buildCampaignEditSubmitPayload,
  campaignSetupStateFingerprint,
  campaignToSetupState,
  emptyMessagingSetup,
  hydrateMessagingFromTemplate,
  messagingFromTemplate,
  type CampaignSetupFormState,
  type PeriodGranularity,
} from './campaignSetupHelpers';

export type CampaignSetupHubSection = 'basics' | 'distribution' | 'respondent';

export interface CampaignSetupFormProps {
  mode: 'create' | 'edit';
  templates?: AeTemplateSummary[];
  campaign?: AeCampaign;
  publicUrl?: string;
  readOnly?: boolean;
  /** When set, hub edit shows only one configuration group per tab. */
  hubSection?: CampaignSetupHubSection;
  /** Add/remove invitees on create wizard only (hub uses Invitations & responses tab). */
  showInviteParticipantFields?: boolean;
  /** Purpose, instructions, closing, and invitation message — editable unless archived. */
  messagingEditable?: boolean;
  /** Benchmark minimum responses — editable even when other setup fields are locked. */
  benchmarkEditable?: boolean;
  /** Hub edit: debounced save on change (no Save button). */
  autoSave?: boolean;
  saving?: boolean;
  submitLabel: string;
  onSubmit: (
    payload: CreateCampaignInput | Partial<CreateCampaignInput>,
    pendingInviteEmails: string,
    options?: { silent?: boolean }
  ) => Promise<void>;
  footerStart?: React.ReactNode;
}

/** Shared campaign setup layout for create and edit flows. */
export const CampaignSetupForm: React.FC<CampaignSetupFormProps> = ({
  mode,
  templates = [],
  campaign,
  readOnly = false,
  showInviteParticipantFields = false,
  hubSection,
  messagingEditable = true,
  benchmarkEditable = true,
  autoSave = false,
  saving = false,
  submitLabel,
  onSubmit,
  footerStart,
}) => {
  const { t } = useLanguage();
  const [openCampaigns, setOpenCampaigns] = useState<Array<{ id: string; name: string }>>([]);
  const [templatePurpose, setTemplatePurpose] = useState('');
  const [templateFraming, setTemplateFraming] = useState<TemplateRespondentFramingDefaults>({
    purpose: '',
    instructions: '',
    closingMessage: '',
  });
  const [state, setState] = useState<CampaignSetupFormState>(() =>
    mode === 'edit' && campaign
      ? campaignToSetupState(campaign)
      : {
          form: {
            template_id: '',
            name: '',
            campaign_type: 'one_off',
            distribution_mode: 'public',
            is_anonymous: false,
            captcha_required: false,
            email_verification_required: true,
            branding: {},
            report_defaults: { show_instant_individual_results: true, benchmark_b1_min_responses: 10 },
            presentation_style: 'question',
            respondent_config: {
              ...AE_DEFAULT_RESPONDENT_CONFIG,
              invite_target_campaign_id: null,
            },
          },
          responsesPerPeriod: false,
          periodGranularity: 'quarter',
          remindOnNewPeriod: false,
          messagingSetup: emptyMessagingSetup(),
          pendingInviteEmails: '',
          adminCompletionDigest: 'every',
          adminCompletionDigestLastSentAt: null,
        }
  );

  const {
    form,
    responsesPerPeriod,
    periodGranularity,
    remindOnNewPeriod,
    messagingSetup,
    pendingInviteEmails,
    adminCompletionDigest,
  } = state;
  const branding = (form.branding ?? {}) as Record<string, string>;
  const reportDefaults = (form.report_defaults ?? {}) as Record<string, boolean | number>;
  const disabled = readOnly || saving;
  const messagingDisabled = !messagingEditable || saving;
  const nameDisabled = saving;
  const templateId = form.template_id;

  useEffect(() => {
    assessmentEngineService
      .listCampaigns({ status: 'open' })
      .then((list) => setOpenCampaigns(list.map((c) => ({ id: c.id, name: c.name }))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (mode !== 'create') return;
    assessmentEngineService
      .getCohortSettings()
      .then((cohort) => {
        setState((prev) => {
          const rd = (prev.form.report_defaults ?? {}) as Record<string, unknown>;
          if (rd.benchmark_b1_min_responses != null) return prev;
          return {
            ...prev,
            form: {
              ...prev.form,
              report_defaults: {
                ...rd,
                benchmark_b1_min_responses: cohort.b1MinResponses,
              },
            },
          };
        });
      })
      .catch(() => {});
  }, [mode]);

  useEffect(() => {
    if (mode === 'create' && templates[0] && !form.template_id) {
      setState((prev) => ({
        ...prev,
        form: { ...prev.form, template_id: templates[0]!.id },
      }));
    }
  }, [mode, templates, form.template_id]);

  useEffect(() => {
    if (mode === 'edit' && campaign) {
      setState((prev) => {
        const base = campaignToSetupState(campaign);
        return {
          ...base,
          messagingSetup: hydrateMessagingFromTemplate(
            base.messagingSetup,
            templateFraming,
            templatePurpose,
            campaign.name
          ),
          // Keep pending emails / local-only fields if any
          pendingInviteEmails: prev.pendingInviteEmails,
        };
      });
    }
  }, [mode, campaign?.id, templateFraming, templatePurpose]);

  useEffect(() => {
    if (autoSave || mode !== 'edit' || !campaign) return;
    setState((prev) => {
      const base = campaignToSetupState(campaign);
      return {
        ...base,
        messagingSetup: hydrateMessagingFromTemplate(
          base.messagingSetup,
          templateFraming,
          templatePurpose,
          campaign.name
        ),
        pendingInviteEmails: prev.pendingInviteEmails,
      };
    });
  }, [autoSave, mode, campaign?.updated_at, campaign, templateFraming, templatePurpose]);

  const messagingSeededForTemplateRef = useRef<string | null>(null);

  useEffect(() => {
    if (!templateId) {
      setTemplatePurpose('');
      setTemplateFraming({ purpose: '', instructions: '', closingMessage: '' });
      messagingSeededForTemplateRef.current = null;
      return;
    }
    assessmentEngineService
      .getTemplate(templateId)
      .then((tpl) => {
        const purpose = templateOpeningPurpose(tpl.default_config);
        const framing = templateRespondentFraming(tpl.default_config);
        setTemplatePurpose(purpose);
        setTemplateFraming(framing);
        setState((prev) => {
          if (mode === 'create') {
            if (messagingSeededForTemplateRef.current === templateId) {
              return prev;
            }
            messagingSeededForTemplateRef.current = templateId;
            return {
              ...prev,
              messagingSetup: messagingFromTemplate(framing, purpose, prev.form.name),
            };
          }
          return {
            ...prev,
            messagingSetup: hydrateMessagingFromTemplate(
              prev.messagingSetup,
              framing,
              purpose,
              prev.form.name
            ),
          };
        });
      })
      .catch(() => {
        setTemplatePurpose('');
        setTemplateFraming({ purpose: '', instructions: '', closingMessage: '' });
      });
  }, [templateId, mode]);

  const patchForm = (patch: Partial<CreateCampaignInput>) => {
    setState((prev) => ({ ...prev, form: { ...prev.form, ...patch } }));
  };

  const patchMessagingSetup = (
    next:
      | CampaignMessagingSetupValue
      | ((prev: CampaignMessagingSetupValue) => CampaignMessagingSetupValue)
  ) => {
    setState((prev) => ({
      ...prev,
      messagingSetup: typeof next === 'function' ? next(prev.messagingSetup) : next,
    }));
  };

  const updateBranding = (key: string, value: string) => {
    patchForm({ branding: { ...(form.branding as object), [key]: value } });
  };

  const canSubmit =
    !!form.template_id &&
    !!form.name.trim() &&
    (mode === 'create' ||
      !readOnly ||
      messagingEditable ||
      benchmarkEditable ||
      !!form.name.trim());

  const stateRef = useRef(state);
  stateRef.current = state;

  const runSubmit = useCallback(
    async (options?: { silent?: boolean }) => {
      const current = stateRef.current;
      const submitAllowed =
        !!current.form.template_id &&
        !!current.form.name.trim() &&
        (mode === 'create' ||
          !readOnly ||
          messagingEditable ||
          benchmarkEditable ||
          !!current.form.name.trim());
      if (!submitAllowed || saving) return false;
      const payload = buildCampaignEditSubmitPayload(current, {
        readOnly,
        messagingEditable,
        benchmarkEditable,
      });
      if (readOnly && Object.keys(payload).length === 0) return false;
      await onSubmit(payload as CreateCampaignInput, current.pendingInviteEmails, options);
      return true;
    },
    [benchmarkEditable, messagingEditable, mode, onSubmit, readOnly, saving]
  );

  const handleSubmit = useCallback(async () => {
    await runSubmit();
  }, [runSubmit]);

  const autoSaveReadyRef = useRef(false);
  const lastSavedFingerprintRef = useRef<string | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (mode !== 'edit' || !autoSave || !hubSection) return;

    const fingerprint = campaignSetupStateFingerprint(state);
    if (!autoSaveReadyRef.current) {
      autoSaveReadyRef.current = true;
      lastSavedFingerprintRef.current = fingerprint;
      return;
    }
    if (fingerprint === lastSavedFingerprintRef.current) return;

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      void (async () => {
        try {
          const ok = await runSubmit({ silent: true });
          if (ok) {
            lastSavedFingerprintRef.current = campaignSetupStateFingerprint(stateRef.current);
          }
        } catch {
          /* Parent shows error toast */
        }
      })();
    }, 700);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [autoSave, hubSection, mode, runSubmit, state]);

  useEffect(() => {
    autoSaveReadyRef.current = false;
    lastSavedFingerprintRef.current = null;
  }, [campaign?.id, hubSection]);

  const selectedTemplate =
    mode === 'edit' && campaign?.template
      ? `${campaign.template.name} (${campaign.template.code})`
      : templates.find((tpl) => tpl.id === templateId);

  const isFullWizard = !hubSection;
  /** Hub tabs use denser controls + multi-column grouping; create wizard stays roomier. */
  const compact = Boolean(hubSection);
  const showBasicsBlock = isFullWizard || hubSection === 'basics';
  const showBrandingBlock = isFullWizard || hubSection === 'basics';
  const showAdminDigestBlock = isFullWizard || hubSection === 'basics';
  const showDistributionBlock = isFullWizard || hubSection === 'distribution';
  const showRespondentBlock = isFullWizard || hubSection === 'respondent';

  const sectionTitle = compact ? AE_SECTION_TITLE_COMPACT : AE_SECTION_TITLE;
  const labelClass = compact ? AE_LABEL_COMPACT : AE_LABEL;
  const inputClass = compact ? AE_INPUT_COMPACT : AE_INPUT;
  const selectClass = compact ? AE_SELECT_COMPACT : AE_SELECT;
  const sectionGap = compact ? 'space-y-2' : 'space-y-4';
  const hintClass = compact ? `${AE_MUTED} text-xs mt-0.5` : `${AE_MUTED} text-sm mt-1`;

  const basicsSection = showBasicsBlock ? (
    <section className={sectionGap}>
      <h2 className={sectionTitle}>{t('assessmentEngine.wizard.sectionBasics')}</h2>
      <div className={compact ? 'grid grid-cols-1 sm:grid-cols-2 gap-2' : 'space-y-4'}>
        <div>
          <label className={labelClass}>{t('assessmentEngine.campaign.name')}</label>
          <input
            className={inputClass}
            value={form.name}
            disabled={nameDisabled}
            onChange={(e) => patchForm({ name: e.target.value })}
          />
        </div>
        <div>
          <label className={labelClass}>{t('assessmentEngine.campaign.template')}</label>
          {mode === 'edit' ? (
            <p className={`text-sm text-[var(--color-surface-text)] ${compact ? 'py-1' : 'py-2'}`}>
              {typeof selectedTemplate === 'string'
                ? selectedTemplate
                : selectedTemplate
                  ? `${selectedTemplate.name} (${selectedTemplate.code})`
                  : campaign?.template_id}
            </p>
          ) : (
            <select
              className={selectClass}
              value={form.template_id}
              disabled={disabled}
              onChange={(e) => patchForm({ template_id: e.target.value })}
            >
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name} ({tpl.code})
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      <div
        className={`${compact ? 'space-y-2 p-2.5' : 'space-y-3 p-4'} border border-[var(--color-border)] rounded-md`}
      >
        <label className="flex items-center gap-2 text-sm text-[var(--color-surface-text)]">
          <input
            type="checkbox"
            className="accent-[var(--color-accent-bg)]"
            checked={responsesPerPeriod}
            disabled={disabled}
            onChange={(e) =>
              setState((prev) => ({ ...prev, responsesPerPeriod: e.target.checked }))
            }
          />
          <span className="font-medium">{t('assessmentEngine.wizard.responsesPerPeriod')}</span>
        </label>

        {responsesPerPeriod && (
          <div className={compact ? 'grid grid-cols-1 sm:grid-cols-2 gap-2 items-end' : 'space-y-3'}>
            <div>
              <label className={labelClass}>{t('assessmentEngine.wizard.periodGranularity')}</label>
              <select
                className={selectClass}
                value={periodGranularity}
                disabled={disabled}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    periodGranularity: e.target.value as PeriodGranularity,
                  }))
                }
              >
                <option value="day">{t('assessmentEngine.period.day')}</option>
                <option value="week">{t('assessmentEngine.period.week')}</option>
                <option value="month">{t('assessmentEngine.period.month')}</option>
                <option value="quarter">{t('assessmentEngine.period.quarter')}</option>
                <option value="year">{t('assessmentEngine.period.year')}</option>
              </select>
            </div>
            <label className="flex items-start gap-2 text-sm text-[var(--color-surface-text)] pb-1">
              <input
                type="checkbox"
                className="mt-0.5 accent-[var(--color-accent-bg)]"
                checked={remindOnNewPeriod}
                disabled={disabled}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, remindOnNewPeriod: e.target.checked }))
                }
              />
              <span className="font-medium">{t('assessmentEngine.wizard.remindOnNewPeriod')}</span>
            </label>
          </div>
        )}
      </div>
    </section>
  ) : null;

  const brandingSection = showBrandingBlock ? (
    <section className={sectionGap}>
      <h2 className={sectionTitle}>{t('assessmentEngine.wizard.sectionBranding')}</h2>
      <div className={compact ? 'grid grid-cols-1 sm:grid-cols-2 gap-2' : 'space-y-4'}>
        <div>
          <label className={labelClass}>{t('assessmentEngine.wizard.orgName')}</label>
          <input
            className={inputClass}
            value={branding.organization_display_name ?? ''}
            disabled={disabled}
            onChange={(e) => updateBranding('organization_display_name', e.target.value)}
          />
        </div>
        <div>
          <label className={labelClass}>{t('assessmentEngine.wizard.logoUrl')}</label>
          <input
            className={inputClass}
            value={branding.logo_url ?? ''}
            disabled={disabled}
            onChange={(e) => updateBranding('logo_url', e.target.value)}
          />
        </div>
      </div>
    </section>
  ) : null;

  const distributionSection = showDistributionBlock ? (
    <section className={sectionGap}>
      <h2 className={sectionTitle}>{t('assessmentEngine.wizard.sectionDistribution')}</h2>
      <CampaignDistributionModeFields
        value={form.distribution_mode ?? 'public'}
        onChange={(distribution_mode) => {
          const respondentConfig = {
            ...(form.respondent_config as AeRespondentConfig),
          };
          if (distribution_mode === 'invite_only' && respondentConfig.completion_options) {
            respondentConfig.completion_options = {
              ...respondentConfig.completion_options,
              allow_share_link: false,
            };
          }
          patchForm({ distribution_mode, respondent_config: respondentConfig });
        }}
        name={`campaign-setup-distribution-${mode}`}
        disabled={disabled}
      />
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <label className="flex items-center gap-2 text-sm text-[var(--color-surface-text)]">
          <input
            type="checkbox"
            checked={form.is_anonymous}
            disabled={disabled}
            onChange={(e) => patchForm({ is_anonymous: e.target.checked })}
          />
          {t('assessmentEngine.wizard.anonymous')}
        </label>
      </div>
    </section>
  ) : null;

  const respondentSetupSection = showRespondentBlock ? (
    <section className={sectionGap}>
      <div>
        <h2 className={sectionTitle}>{t('assessmentEngine.wizard.sectionRespondent')}</h2>
        <p className={hintClass}>{t('assessmentEngine.wizard.sectionRespondentHint')}</p>
      </div>
      <CampaignRespondentSetupFields
        value={{
          presentation_style: form.presentation_style ?? 'question',
          respondent_config:
            (form.respondent_config as typeof AE_DEFAULT_RESPONDENT_CONFIG) ??
            AE_DEFAULT_RESPONDENT_CONFIG,
        }}
        onChange={(next) =>
          patchForm({
            presentation_style: next.presentation_style,
            respondent_config: next.respondent_config,
          })
        }
        emailVerificationRequired={form.email_verification_required !== false}
        onEmailVerificationChange={(enabled) =>
          patchForm({ email_verification_required: enabled })
        }
        emailVerificationEditable={messagingEditable && !saving}
        showInstantResults={!!reportDefaults.show_instant_individual_results}
        onInstantResultsChange={(enabled) =>
          patchForm({
            report_defaults: { ...reportDefaults, show_instant_individual_results: enabled },
          })
        }
        benchmarkMinResponses={Number(reportDefaults.benchmark_b1_min_responses ?? 10)}
        onBenchmarkMinResponsesChange={(value) =>
          patchForm({
            report_defaults: { ...reportDefaults, benchmark_b1_min_responses: value },
          })
        }
        distributionMode={form.distribution_mode ?? 'public'}
        inviteTargetCampaigns={openCampaigns}
        currentCampaignId={campaign?.id}
        defaultInviteTargetSelf={mode === 'create'}
        disabled={disabled}
        benchmarkMinEditable={benchmarkEditable && !saving}
        compact={compact}
      />
    </section>
  ) : null;

  const respondentCopySection = showRespondentBlock ? (
    <section className={compact ? 'space-y-2' : 'space-y-6'}>
      <div>
        <h2 className={sectionTitle}>{t('assessmentEngine.wizard.sectionRespondentCopy')}</h2>
        <p className={hintClass}>{t('assessmentEngine.wizard.sectionRespondentCopyHint')}</p>
      </div>
      <CampaignInviteSetupFields
        value={messagingSetup}
        onChange={patchMessagingSetup}
        disabled={messagingDisabled}
        compact={compact}
        assessmentName={form.name}
      />
    </section>
  ) : null;

  const adminDigestSection = showAdminDigestBlock ? (
    <CampaignAdminDigestFields
      value={adminCompletionDigest}
      onChange={(next) => setState((prev) => ({ ...prev, adminCompletionDigest: next }))}
      disabled={messagingDisabled}
      compact={compact}
    />
  ) : null;

  const inviteListSection =
    showInviteParticipantFields && isFullWizard ? (
      <CampaignInviteListFields
        pendingEmails={pendingInviteEmails}
        onPendingEmailsChange={(text) =>
          setState((prev) => ({ ...prev, pendingInviteEmails: text }))
        }
      />
    ) : null;

  /** Hub: fill width with columns; avoid tall single-column stacks of short fields. */
  const formBody =
    hubSection === 'basics' ? (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        <div className="space-y-4">
          {basicsSection}
          {brandingSection}
        </div>
        <div className="space-y-4">{adminDigestSection}</div>
      </div>
    ) : hubSection === 'respondent' ? (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 xl:gap-6">
        {respondentSetupSection}
        {respondentCopySection}
      </div>
    ) : hubSection === 'distribution' ? (
      <div className="max-w-3xl">{distributionSection}</div>
    ) : (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-6 gap-y-6">
        <div className="space-y-6">
          {basicsSection}
          {brandingSection}
        </div>
        <div className="space-y-6">
          {distributionSection}
          {respondentSetupSection}
        </div>
        <div className="space-y-6">
          {respondentCopySection}
          {adminDigestSection}
          {inviteListSection}
        </div>
      </div>
    );

  return (
    <>
      {formBody}

      {mode === 'edit' && autoSave && hubSection ? (
        saving ? (
          <p className={`${AE_MUTED} text-sm pt-2`}>{t('assessmentEngine.hub.autoSaving')}</p>
        ) : null
      ) : (
        <div className="flex justify-between pt-6 mt-8 border-t border-[var(--color-border)]">
          {footerStart ?? <span />}
          {(!readOnly || messagingEditable || benchmarkEditable) && (
            <button
              type="button"
              disabled={!canSubmit || saving}
              className={AE_BTN_PRIMARY}
              onClick={() => void handleSubmit()}
            >
              {saving ? t('assessmentEngine.campaign.creating') : submitLabel}
            </button>
          )}
        </div>
      )}
    </>
  );
};

export { parseInviteEmailList } from './campaignSetupHelpers';
